import { WELLBEING_LIMITS, WELLBEING_SCENARIOS } from '../constants/wellbeingMessages'
import {
	canShowWellbeingPopup,
	getIsoWeekKey,
	readRiskSession,
	readWellbeingState,
	todayKey,
} from './wellbeingStorage'

/**
 * Pure decision helper — no side effects.
 * @param {object} input
 */
export function evaluateWellbeingTrigger(input = {}) {
	const {
		now = new Date(),
		pathname = '',
		isIdle = false,
		hasOpenModal = false,
		hasActiveSession = false,
		sessionsBeforeLunchCompleted = 0,
		todaysSessionCount = 0,
		remainingSessions = 0,
		betweenSessions = false,
	} = input

	if (!pathname.includes('/dashboard')) {
		return null
	}

	// Only the counsellor home dashboard — never interrupt other workflows
	const onDashboardHome =
		pathname === '/dashboard/home' ||
		pathname.endsWith('/dashboard/home')

	if (!onDashboardHome) return null

	if (hasActiveSession) return null
	if (hasOpenModal) return null
	if (!isIdle) return null

	const gate = canShowWellbeingPopup(now.getTime())
	if (!gate.ok) return null

	const state = gate.state || readWellbeingState()
	const hour = now.getHours()
	const minutes = now.getMinutes()
	const timeValue = hour * 60 + minutes

	// Scenario 3 — risk flag (10 min after difficult session)
	const risk = readRiskSession()
	if (
		risk?.endedAt &&
		now.getTime() - risk.endedAt >= WELLBEING_LIMITS.RISK_DELAY_MS &&
		state.riskShownForId !== risk.sessionId
	) {
		return {
			scenario: WELLBEING_SCENARIOS.RISK,
			meta: { riskSessionId: risk.sessionId },
		}
	}

	// Scenario 4 — late evening login on dashboard
	if (hour >= 18 && state.eveningShownDate !== todayKey(now)) {
		return { scenario: WELLBEING_SCENARIOS.EVENING }
	}

	// Scenario 1 — midday after 2+ morning sessions (12:00–14:30)
	if (
		timeValue >= 12 * 60 &&
		timeValue <= 14 * 60 + 30 &&
		sessionsBeforeLunchCompleted >= 2 &&
		state.middayShownWeek !== getIsoWeekKey(now)
	) {
		return { scenario: WELLBEING_SCENARIOS.MIDDAY }
	}

	// Scenario 2 — heavy day, between sessions
	if (
		todaysSessionCount >= 4 &&
		betweenSessions &&
		remainingSessions > 0 &&
		state.heavyShownDate !== todayKey(now)
	) {
		return { scenario: WELLBEING_SCENARIOS.HEAVY }
	}

	return null
}

export function isActiveCounsellingPath(pathname = '') {
	return (
		pathname.includes('/live') ||
		pathname.includes('/ai-counselling') ||
		pathname.includes('/manual-counselling') ||
		pathname.includes('/session/') ||
		pathname.includes('/counselling/room') ||
		pathname.includes('/meeting/')
	)
}

export function detectOpenModal(excludeIds = []) {
	if (typeof document === 'undefined') return false
	const modals = document.querySelectorAll('.MuiModal-root')
	for (const modal of modals) {
		const id = modal.getAttribute('aria-labelledby') || modal.id || ''
		if (excludeIds.some((ex) => id.includes(ex) || modal.id?.includes(ex))) {
			continue
		}
		// Hidden modals still in DOM
		const style = window.getComputedStyle(modal)
		if (style.visibility === 'hidden' || style.display === 'none') continue
		return true
	}
	return false
}
